/**
 * API собственной номенклатуры: товары, группы, виды цен и цены (чтение и правка).
 * Данные кэшируются по ресурсу и записи, правка сбрасывает только затронутые теги.
 */
import { baseApi, resourceListTag, resourceScopeTag, resourceTag } from '@krp/client-runtime'

import {
  decimalOf,
  decodeGroup,
  decodeGroups,
  decodePriceKind,
  decodePriceKinds,
  decodePriceRecord,
  decodePriceRecords,
  decodeProduct,
  decodeProductsPage,
  type OwnGroup,
  type OwnPriceKind,
  type OwnPriceOwner,
  type OwnPriceRecord,
  type OwnProduct,
  type OwnProductInput,
  type OwnProductsPage,
} from './contracts'
import { OWN_NOMENCLATURE_URLS as URLS } from './urls'

/** Теги своей номенклатуры: `<rid>:<id>` и `<rid>:LIST` — правка в одном ресурсе не сбрасывает соседний. */
export const OWN_PRODUCT_TAG = 'OwnProduct' as const
export const OWN_GROUP_TAG = 'OwnGroup' as const
export const OWN_PRICE_TAG = 'OwnPrice' as const

export interface OwnProductsArgs {
  resourceId: string
  cursor: string | null
  limit: number
  /** Артикул или штрихкод целиком: поиска по части названия в службе нет (CLAUDE.md §8 — текстовый поиск только единый). */
  sku: string | null
  barcode: string | null
  groupId: string | null
  /** `false` — только действующие, `null` — вместе с архивом. */
  archived: boolean | null
}

export interface OwnProductArgs {
  resourceId: string
  productId: string
  /** Дата цен карточки (`yyyy-MM-dd`); `null` — сегодня по календарю магазина. */
  on: string | null
}

export interface OwnProductCreateArgs {
  resourceId: string
  product: OwnProductInput
}

export interface OwnProductChangeArgs {
  resourceId: string
  productId: string
  expectedVersion: number
  product: OwnProductInput
}

export interface OwnProductVersionArgs {
  resourceId: string
  productId: string
  expectedVersion: number
}

export interface OwnGroupCreateArgs {
  resourceId: string
  name: string
  parentId: string | null
}

export interface OwnGroupChangeArgs {
  resourceId: string
  groupId: string
  expectedVersion: number
  name: string
  parentId: string | null
  archived: boolean
}

export interface OwnPriceKindCreateArgs {
  resourceId: string
  name: string
  currency: string
  owner: OwnPriceOwner
}

export interface OwnPriceKindChangeArgs {
  resourceId: string
  typeId: string
  expectedVersion: number
  name: string
  currency: string
  archived: boolean
  owner: OwnPriceOwner
}

export interface OwnPricesArgs {
  resourceId: string
  productId: string
  /** `null` — вся история записей, новые сверху; дата — действующие на неё по каждому виду. */
  on: string | null
}

export interface OwnPriceSetArgs {
  resourceId: string
  productId: string
  priceTypeId: string
  validFrom: string
  kopecks: number
}

export interface OwnPriceDeleteArgs {
  resourceId: string
  productId: string
  priceTypeId: string
  validFrom: string
}

interface ResourceArgs {
  resourceId: string
}

const productTags = (resourceId: string, productId: string) => [
  resourceTag(OWN_PRODUCT_TAG, resourceId, productId),
  resourceListTag(OWN_PRODUCT_TAG, resourceId),
  resourceTag(OWN_PRICE_TAG, resourceId, productId),
]

export const ownNomenclatureApi = baseApi
  .enhanceEndpoints({ addTagTypes: [OWN_PRODUCT_TAG, OWN_GROUP_TAG, OWN_PRICE_TAG] })
  .injectEndpoints({
    endpoints: (build) => ({
      ownProducts: build.query<OwnProductsPage, OwnProductsArgs>({
        query: ({ resourceId, cursor, limit, sku, barcode, groupId, archived }) => ({
          url: URLS.products(resourceId),
          params: {
            limit,
            ...(cursor === null ? {} : { cursor }),
            ...(sku === null ? {} : { sku }),
            ...(barcode === null ? {} : { barcode }),
            ...(groupId === null ? {} : { groupId }),
            ...(archived === null ? {} : { archived }),
          },
          decode: decodeProductsPage,
        }),
        providesTags: (_result, _error, { resourceId }) => [resourceListTag(OWN_PRODUCT_TAG, resourceId), resourceScopeTag(resourceId)],
      }),
      ownProduct: build.query<OwnProduct, OwnProductArgs>({
        query: ({ resourceId, productId, on }) => ({
          url: URLS.product(resourceId, productId),
          params: on === null ? {} : { on },
          decode: decodeProduct,
        }),
        providesTags: (_result, _error, { resourceId, productId }) => [
          resourceTag(OWN_PRODUCT_TAG, resourceId, productId),
          resourceTag(OWN_PRICE_TAG, resourceId, productId),
          resourceScopeTag(resourceId),
        ],
      }),
      ownCreateProduct: build.mutation<OwnProduct, OwnProductCreateArgs>({
        query: ({ resourceId, product }) => ({ url: URLS.products(resourceId), method: 'POST', body: product, decode: decodeProduct }),
        invalidatesTags: (_result, _error, { resourceId }) => [resourceListTag(OWN_PRODUCT_TAG, resourceId)],
      }),
      ownChangeProduct: build.mutation<OwnProduct, OwnProductChangeArgs>({
        query: ({ resourceId, productId, expectedVersion, product }) => ({
          url: URLS.product(resourceId, productId),
          method: 'PUT',
          body: { expectedVersion, product },
          decode: decodeProduct,
        }),
        invalidatesTags: (_result, _error, { resourceId, productId }) => productTags(resourceId, productId),
      }),
      ownArchiveProduct: build.mutation<OwnProduct, OwnProductVersionArgs>({
        query: ({ resourceId, productId, expectedVersion }) => ({
          url: URLS.archive(resourceId, productId),
          method: 'POST',
          body: { expectedVersion },
          decode: decodeProduct,
        }),
        invalidatesTags: (_result, _error, { resourceId, productId }) => productTags(resourceId, productId),
      }),
      ownRestoreProduct: build.mutation<OwnProduct, OwnProductVersionArgs>({
        query: ({ resourceId, productId, expectedVersion }) => ({
          url: URLS.restore(resourceId, productId),
          method: 'POST',
          body: { expectedVersion },
          decode: decodeProduct,
        }),
        invalidatesTags: (_result, _error, { resourceId, productId }) => productTags(resourceId, productId),
      }),
      ownGroups: build.query<readonly OwnGroup[], ResourceArgs>({
        query: ({ resourceId }) => ({ url: URLS.groups(resourceId), decode: decodeGroups }),
        providesTags: (_result, _error, { resourceId }) => [resourceListTag(OWN_GROUP_TAG, resourceId), resourceScopeTag(resourceId)],
      }),
      ownCreateGroup: build.mutation<OwnGroup, OwnGroupCreateArgs>({
        query: ({ resourceId, name, parentId }) => ({
          url: URLS.groups(resourceId),
          method: 'POST',
          body: { name, parentId },
          decode: (raw) => decodeGroup(raw),
        }),
        invalidatesTags: (_result, _error, { resourceId }) => [resourceListTag(OWN_GROUP_TAG, resourceId)],
      }),
      ownChangeGroup: build.mutation<OwnGroup, OwnGroupChangeArgs>({
        query: ({ resourceId, groupId, expectedVersion, name, parentId, archived }) => ({
          url: URLS.group(resourceId, groupId),
          method: 'PUT',
          body: { expectedVersion, name, parentId, archived },
          decode: (raw) => decodeGroup(raw),
        }),
        invalidatesTags: (_result, _error, { resourceId }) => [resourceListTag(OWN_GROUP_TAG, resourceId)],
      }),
      ownPriceKinds: build.query<readonly OwnPriceKind[], ResourceArgs>({
        query: ({ resourceId }) => ({ url: URLS.priceTypes(resourceId), decode: decodePriceKinds }),
        providesTags: (_result, _error, { resourceId }) => [resourceListTag(OWN_PRICE_TAG, resourceId), resourceScopeTag(resourceId)],
      }),
      ownCreatePriceKind: build.mutation<OwnPriceKind, OwnPriceKindCreateArgs>({
        query: ({ resourceId, name, currency, owner }) => ({
          url: URLS.priceTypes(resourceId),
          method: 'POST',
          body: { name, currency, owner },
          decode: (raw) => decodePriceKind(raw),
        }),
        invalidatesTags: (_result, _error, { resourceId }) => [resourceListTag(OWN_PRICE_TAG, resourceId)],
      }),
      ownChangePriceKind: build.mutation<OwnPriceKind, OwnPriceKindChangeArgs>({
        query: ({ resourceId, typeId, expectedVersion, name, currency, archived, owner }) => ({
          url: URLS.priceType(resourceId, typeId),
          method: 'PUT',
          body: { expectedVersion, name, currency, archived, owner },
          decode: (raw) => decodePriceKind(raw),
        }),
        invalidatesTags: (_result, _error, { resourceId }) => [resourceListTag(OWN_PRICE_TAG, resourceId)],
      }),
      ownPrices: build.query<readonly OwnPriceRecord[], OwnPricesArgs>({
        query: ({ resourceId, productId, on }) => ({
          url: URLS.prices(resourceId),
          params: { productId, ...(on === null ? {} : { on }) },
          decode: decodePriceRecords,
        }),
        providesTags: (_result, _error, { resourceId, productId }) => [resourceTag(OWN_PRICE_TAG, resourceId, productId), resourceScopeTag(resourceId)],
      }),
      ownSetPrice: build.mutation<OwnPriceRecord, OwnPriceSetArgs>({
        query: ({ resourceId, productId, priceTypeId, validFrom, kopecks }) => ({
          url: URLS.prices(resourceId),
          method: 'POST',
          body: { productId, priceTypeId, validFrom, price: decimalOf(kopecks) },
          decode: (raw) => decodePriceRecord(raw),
        }),
        invalidatesTags: (_result, _error, { resourceId, productId }) => [resourceTag(OWN_PRICE_TAG, resourceId, productId)],
      }),
      ownDeletePrice: build.mutation<null, OwnPriceDeleteArgs>({
        query: ({ resourceId, productId, priceTypeId, validFrom }) => ({
          url: URLS.prices(resourceId),
          method: 'DELETE',
          params: { productId, priceTypeId, validFrom },
        }),
        invalidatesTags: (_result, _error, { resourceId, productId }) => [resourceTag(OWN_PRICE_TAG, resourceId, productId)],
      }),
    }),
  })

export const {
  ownProducts,
  ownProduct,
  ownCreateProduct,
  ownChangeProduct,
  ownArchiveProduct,
  ownRestoreProduct,
  ownGroups,
  ownCreateGroup,
  ownChangeGroup,
  ownPriceKinds,
  ownCreatePriceKind,
  ownChangePriceKind,
  ownPrices,
  ownSetPrice,
  ownDeletePrice,
} = ownNomenclatureApi.endpoints
